import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useDependencies } from "src/apps/hooks/useDependencies";
import { useAuth } from "src/apps/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDocumentationAreaIdAndActorId } from "../hooks/useDocumentationAreaIdAndActorId";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { LoadingScreen } from "../components/loading/LoadingScreen";
import { DOCUMENTATION_AREA_ROUTES } from "../routes/Navigation";
import { DocumentationArea } from "src/contexts/documentationArea/domain/DocumentationArea";
import { useHasSignedAcceptance } from "../hooks/useHasSignedAcceptance";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import {
  getDocumentationAreaRemovalReason,
  getUnavailableLocationState,
} from "src/apps/utils/getDocumentationAreaRemovalReason";
import { useLocale } from "../hooks/useLocale";
import { PublicEventNames } from "../hooks/post-message-event/PublicEventNames";
import { usePostMessageEvent } from "../hooks/post-message-event/usePostMessageEvent";
import { useGetDataFromPermissoToken } from "../hooks/useGetDataFromPermissoToken";
import mixpanel from "mixpanel-browser";
import { ActorId } from "src/contexts/documentationArea/domain/ActorId";
import { useBrowserLocalStorage } from "src/apps/hooks/useBrowserLocalStorage";
import { getDocumentationAreaPath } from "../routes/getDocumentationAreaPath";
import { DocumentationAreaActorBasicData } from "src/contexts/documentationArea/domain/DocumentationAreaActorBasicData";

const REFETCH_INTERVAL = 3 * 1000;

type DocumentationAreaContextType = {
  documentationArea: DocumentationArea | null;
  actorBasicData: DocumentationAreaActorBasicData | null;
  setIsDocumentationAreaQueryEnabled: (value: boolean) => void;
  selectedActorId: ActorId | null;
  setSelectedActorId: (value: ActorId) => void;
};

export const DocumentationAreaContext = createContext<DocumentationAreaContextType>(
  {} as DocumentationAreaContextType,
);

export function DocumentationAreaProvider({ children }: { children: ReactNode }) {
  const { documentationAreaId, actorId, documentationAreaIdError, isDocumentationAreaIdLoading } =
    useDocumentationAreaIdAndActorId();

  const {
    hasSignedAcceptance,
    isSignedAcceptanceLoading,
    isSignedAcceptanceSuccess,
    isSignedAcceptanceError,
    error: signedAcceptanceError,
  } = useHasSignedAcceptance();
  const { token, deleteToken, isLoginlessAccessToken } = useAuth();
  const { locale, setLocale } = useLocale();
  const { documentationAreaController } = useDependencies();
  const navigate = useNavigate();
  const location = useLocation();
  const { postMessageEvent } = usePostMessageEvent();
  const tokenData = useGetDataFromPermissoToken();
  const [isDocumentationAreaQueryEnabled, setIsDocumentationAreaQueryEnabled] = useState(true);
  const [tabSessionId] = useState(() => {
    if (typeof window === "undefined") {
      return "server";
    }

    const existingId = window.sessionStorage.getItem("tab_session_id");
    if (existingId) {
      return existingId;
    }

    const newId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    window.sessionStorage.setItem("tab_session_id", newId);
    return newId;
  });
  const [storedSelectedActorId, setStoredSelectedActorId] = useBrowserLocalStorage<ActorId | null>(
    `stored_query_param.selected_actor_id.${documentationAreaId}.${tabSessionId}`,
    null,
  );
  const [selectedActorId, setSelectedActorId] = useState<ActorId | null>(storedSelectedActorId);
  const hasStartedSessionRecording = useRef(false);
  const queryClient = useQueryClient();

  const handleSetSelectedActorId = useCallback(
    (actorId: ActorId) => {
      setSelectedActorId(actorId);
      setStoredSelectedActorId(actorId);

      if (!documentationAreaId) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: ["DocumentationArea", documentationAreaId, actorId],
      });
    },
    [documentationAreaId, queryClient, setStoredSelectedActorId],
  );

  useEffect(() => {
    setSelectedActorId(storedSelectedActorId);
  }, [documentationAreaId, storedSelectedActorId]);

  useEffect(() => {
    if (actorId) {
      mixpanel.identify(actorId);
    } else if (documentationAreaId) {
      mixpanel.identify(documentationAreaId);
    }
  }, [actorId, documentationAreaId]);

  useEffect(() => {
    if (selectedActorId) {
      return;
    }

    if (storedSelectedActorId) {
      setSelectedActorId(storedSelectedActorId);
      return;
    }

    if (actorId) {
      handleSetSelectedActorId(actorId);
      return;
    }

    if (tokenData?.actorId) {
      handleSetSelectedActorId(tokenData.actorId);
    }
  }, [
    actorId,
    tokenData?.actorId,
    selectedActorId,
    storedSelectedActorId,
    handleSetSelectedActorId,
  ]);

  const queryFn = async () => {
    if (!documentationAreaId) {
      throw new Error("Calling /GET DocumentationArea without DocumentationAreaId");
    }
    if (!selectedActorId) {
      throw new Error("Calling /GET DocumentationArea without actorId");
    }
    const response = await documentationAreaController.get(
      token,
      documentationAreaId,
      selectedActorId,
      locale,
    );
    const responseLocale = response.getLocale();
    if (responseLocale) {
      setLocale(responseLocale);
    }
    return response;
  };

  const {
    data: documentationArea,
    error: documentationAreaError,
    isLoading: isDocumentationAreaLoading,
  } = useQuery({
    queryKey: ["DocumentationArea", documentationAreaId, selectedActorId, locale],
    queryFn,
    refetchInterval: REFETCH_INTERVAL,
    enabled: Boolean(documentationAreaId && selectedActorId) && isDocumentationAreaQueryEnabled,
    retry: false,
  });

  const {
    data: actorBasicData,
    error: actorBasicDataError,
    isLoading: isActorBasicDataLoading,
  } = useQuery({
    queryKey: ["DocumentationAreaActorBasicData", documentationAreaId, selectedActorId],
    queryFn: async () => {
      if (!documentationAreaId) {
        throw new Error("Calling /GET ActorBasicData without DocumentationAreaId");
      }
      if (!selectedActorId) {
        throw new Error("Calling /GET ActorBasicData without actorId");
      }

      return await documentationAreaController.getActorBasicData(
        token,
        documentationAreaId,
        selectedActorId,
      );
    },
    enabled: Boolean(documentationAreaId && selectedActorId) && isDocumentationAreaQueryEnabled,
    retry: false,
  });

  useEffect(() => {
    if (!documentationArea || hasStartedSessionRecording.current) {
      return;
    }

    if (documentationArea.isForCompanies()) {
      mixpanel.start_session_recording();
      hasStartedSessionRecording.current = true;
    }
  }, [documentationArea]);

  useEffect(() => {
    if (!documentationAreaIdError) {
      return;
    }

    if (!(documentationAreaIdError instanceof HTTPError)) {
      return;
    }

    const removalReason = getDocumentationAreaRemovalReason(documentationAreaIdError);
    if (removalReason) {
      navigate(
        {
          pathname: DOCUMENTATION_AREA_ROUTES.DOCUMENTATION_AREA_UNAVAILABLE_SCREEN,
          search: location.search,
        },
        { state: getUnavailableLocationState(removalReason), replace: true },
      );
      return;
    }

    const httpCode = documentationAreaIdError.getHTTPCode();
    // A refetch failing without a removal code while we already have the ids: the area query
    // decides what to show, so the unavailable screen is not overridden by not-found
    if (httpCode.isNotFound() && documentationAreaId) {
      return;
    }

    if (httpCode.isBadRequest() || httpCode.isNotFound()) {
      navigate({ pathname: DOCUMENTATION_AREA_ROUTES.NOT_FOUND_SCREEN, search: location.search });
      return;
    }

    const auth0TokenExpired = !isLoginlessAccessToken && httpCode.isUnauthorized();
    if (httpCode.isForbidden() || auth0TokenExpired) {
      deleteToken();
      return;
    }
  }, [documentationAreaIdError]);

  const sendPostMessage = () => {
    if (!tokenData) {
      throw new Error("No data from Permisso token");
    }
    const { documentationAreaId, userId } = tokenData;

    postMessageEvent({
      name: PublicEventNames.TOKEN_EXPIRED,
      extraData: {
        documentationAreaId,
        userId,
      },
    });
  };

  useEffect(() => {
    if (!documentationAreaError) {
      return;
    }

    if (!(documentationAreaError instanceof HTTPError)) {
      return;
    }

    const httpCode = documentationAreaError.getHTTPCode();
    const loginlessTokenExpired = isLoginlessAccessToken && httpCode.isUnauthorized();

    if (httpCode.isNotFound()) {
      navigate(
        {
          pathname: DOCUMENTATION_AREA_ROUTES.DOCUMENTATION_AREA_UNAVAILABLE_SCREEN,
          search: location.search,
        },
        { state: getUnavailableLocationState(documentationAreaError.getCode()) },
      );
      return;
    }

    if (httpCode.isBadRequest()) {
      navigate({ pathname: DOCUMENTATION_AREA_ROUTES.NOT_FOUND_SCREEN, search: location.search });
      return;
    }

    if (loginlessTokenExpired) {
      sendPostMessage();
      navigate({ pathname: DOCUMENTATION_AREA_ROUTES.NOT_FOUND_SCREEN, search: location.search });
      return;
    }
  }, [documentationAreaError]);

  useEffect(() => {
    if (!(actorBasicDataError instanceof HTTPError)) {
      return;
    }

    if (actorBasicDataError.getHTTPCode().isNotFound()) {
      navigate(
        {
          pathname: DOCUMENTATION_AREA_ROUTES.DOCUMENTATION_AREA_UNAVAILABLE_SCREEN,
          search: location.search,
        },
        { state: getUnavailableLocationState(actorBasicDataError.getCode()) },
      );
    }
  }, [actorBasicDataError]);

  if (
    !documentationArea ||
    isDocumentationAreaLoading ||
    isDocumentationAreaIdLoading ||
    isActorBasicDataLoading
  ) {
    return <LoadingScreen />;
  }

  if (isSignedAcceptanceError) {
    const removalReason = getDocumentationAreaRemovalReason(signedAcceptanceError);
    return (
      <Navigate
        replace
        state={removalReason ? getUnavailableLocationState(removalReason) : undefined}
        to={{
          pathname: removalReason
            ? DOCUMENTATION_AREA_ROUTES.DOCUMENTATION_AREA_UNAVAILABLE_SCREEN
            : DOCUMENTATION_AREA_ROUTES.NOT_FOUND_SCREEN,
          search: location.search,
        }}
      />
    );
  }

  if (isSignedAcceptanceLoading || !isSignedAcceptanceSuccess) {
    return <LoadingScreen />;
  }

  const pathToRedirect = getDocumentationAreaPath(
    documentationArea,
    hasSignedAcceptance || false,
    actorBasicData?.isFullfilled,
  );

  if (location.pathname !== pathToRedirect) {
    return <Navigate replace to={{ pathname: pathToRedirect, search: location.search }} />;
  }

  return (
    <DocumentationAreaContext.Provider
      value={{
        documentationArea,
        actorBasicData: actorBasicData ?? null,
        setIsDocumentationAreaQueryEnabled,
        selectedActorId,
        setSelectedActorId: handleSetSelectedActorId,
      }}
    >
      {children}
    </DocumentationAreaContext.Provider>
  );
}
